// Backend Rule (with its scope/eligibility relations included) → front rule
// shape. Reverse of save.adaptor.js.

// Eligibility rows back into the shape the pickers hold in state. The pickers
// were adapted from campaignV4 and still carry Discounty's row shape
// ({ id, isActive, isExclude, name, isDeleted }); `id` is where they keep the
// SHOPIFY identifier, which is what selection matching compares.
//
// `isExclude` is deliberately false here rather than read from the row: MOQ's
// eligibility tables have no exclude column at all. Include-only is the whole
// model, and inventing the field on read would imply a capability that does not
// exist in the schema.
const eligibilityRow = (over) => ({
  id: '',
  isActive: true,
  isExclude: false,
  name: '',
  isDeleted: false,
  ...over,
});

const fromCustomers = (rows) =>
  (rows ?? []).map((c) =>
    eligibilityRow({
      id: c.shopifyId,
      shopifyId: c.shopifyId,
      firstName: c.firstName ?? undefined,
      lastName: c.lastName ?? undefined,
      meta: c.meta ?? undefined,
      // The picker labels customers by email and stores it in `name`.
      name: c.meta?.email ?? [c.firstName, c.lastName].filter(Boolean).join(' '),
    }),
  );

// The tag picker uses the tag string as both id and label.
const fromCustomerTags = (rows) =>
  (rows ?? []).map((t) => eligibilityRow({ id: t.tagName, name: t.tagName, tagName: t.tagName }));

const fromCustomerSegments = (rows) =>
  (rows ?? []).map((s) =>
    eligibilityRow({ id: s.shopifyId, shopifyId: s.shopifyId, name: s.name }),
  );

const fromCountries = (rows) =>
  (rows ?? []).map((c) =>
    eligibilityRow({ id: c.countryCode, name: c.countryCode, countryCode: c.countryCode }),
  );

// Backend RuleProduct rows are one per (productId, variantId) pair — regroup
// into one row per product (with a variantIdList/variants array) to match
// what ResourcePickerField.js/ResourceListWithDelete.js/ResourcePickerModal.js
// actually expect on rule.scopeProducts (mirrors utils/mapData.js's
// mapProductstoSave shape, see save.adaptor.js's mapProducts for the reverse).
function fromProducts(products) {
  const byProduct = new Map();

  (products ?? []).forEach((p) => {
    const productId = p.productId?.toString();
    const isExclude = Boolean(p.isExcluded);
    const variantId = p.variantId?.toString();
    const key = `${productId}:${isExclude}`;

    // p.meta.variantTitle is this row's own variant title ("XL", "L", ...) —
    // NOT the product title. Falls back to '' (single/default-variant
    // product) rather than repeating the product name.
    const variantTitle = p.meta?.variantTitle ?? '';

    // Rule precedence bulk "Exclude these from my rule" (docs/rule-precedence-
    // implementation-plan.md §3.5 item 1) — carried per-variant, not per-
    // product: a product can have some hand-picked exclusions alongside
    // auto-written ones. Must round-trip through save.adaptor.js's
    // mapProducts or a save from the editor silently turns every
    // auto-exclusion back into a manual one (same failure class as the
    // eligibility stripId incident this file's header comment documents).
    // conflictedRule (name/isActive) is resolved server-side only
    // (RuleService.get()) — a live snapshot of whatever conflictedRuleId
    // still points at, so AutomaticExclusionsList.js can show "Active
    // elsewhere" vs "No longer active" without a second lookup. Never sent
    // back on save (save.adaptor.js's mapProducts only reads conflictedRuleId).
    const variantEntry = {
      productId,
      id: variantId,
      title: variantTitle,
      isAutomatic: Boolean(p.isAutomatic),
      conflictedRuleId: p.conflictedRuleId ?? null,
      conflictedRuleName: p.conflictedRule?.name ?? null,
      conflictedRuleActive: p.conflictedRule?.isActive ?? null,
    };

    const existing = byProduct.get(key);
    if (existing) {
      existing.variantIdList.push(variantId);
      existing.variants.push(variantEntry);
    } else {
      byProduct.set(key, {
        productId,
        isExclude,
        title: p.meta?.title ?? '',
        handle: p.meta?.handle ?? '',
        image: { src: p.meta?.images?.[0]?.originalSrc ?? null },
        product: { productId, title: p.meta?.title ?? '', handle: p.meta?.handle ?? '' },
        variantIdList: [variantId],
        variants: [variantEntry],
      });
    }
  });

  return [...byProduct.values()];
}

function fromCollections(collections) {
  return (collections ?? []).map((c) => ({
    collectionId: c.collectionId?.toString(),
    isExclude: Boolean(c.isExcluded),
    title: c.meta?.title ?? '',
    collection: { title: c.meta?.title ?? '' },
  }));
}

function fromTags(tags) {
  return (tags ?? []).map((t) => ({ tagName: t.tagName, isExclude: Boolean(t.isExcluded) }));
}

function fromVendors(vendors) {
  return (vendors ?? []).map((v) => ({ vendorId: v.vendorName, isExclude: Boolean(v.isExcluded) }));
}

function fromProductTypes(productTypes) {
  return (productTypes ?? []).map((t) => ({ productTypeName: t.productTypeName, isExclude: Boolean(t.isExcluded) }));
}

function fromPriceRanges(priceRanges) {
  return (priceRanges ?? []).map((r) => ({ fromPrice: r.fromPrice, toPrice: r.toPrice, isExclude: Boolean(r.isExcluded) }));
}

function fromVariantMetafieldConditions(variantMetafieldConditions) {
  return (variantMetafieldConditions ?? []).map((c) => ({
    namespace: c.namespace,
    key: c.key,
    operator: c.operator,
    value: c.value,
    isExclude: Boolean(c.isExcluded),
  }));
}

export function singleRuleFormAdaptor(rule) {
  const data = {
    id: rule.id,
    shopId: rule.shopId,
    name: rule.name,
    type: rule.type,
    status: rule.status,

    min: rule.min ?? undefined,
    max: rule.max ?? undefined,
    multiple: rule.multiple ?? undefined,
    minValue: rule.minValue != null ? Number(rule.minValue) : undefined,
    maxValue: rule.maxValue != null ? Number(rule.maxValue) : undefined,
    minWeight: rule.minWeight != null ? Number(rule.minWeight) : undefined,
    maxWeight: rule.maxWeight != null ? Number(rule.maxWeight) : undefined,
    weightUnit: rule.weightUnit ?? 'lb',
    repurchasePeriod: rule.repurchasePeriod ?? 'none',
    applyMode: rule.applyMode ?? 'each_item',
    countVariantsSeparately: rule.countVariantsSeparately ?? true,

    isImmediately: rule.isImmediately ?? true,
    startDate: rule.startAt,
    endDate: rule.endAt,

    scopeType: rule.scopeType ?? 'all_store',
    hasExcludes: Boolean(rule.hasExcludes),
    scopeExcludeType: rule.scopeExcludeType,
    includeOnFly: Boolean(rule.includeOnFly),
    excludeOnFly: Boolean(rule.excludeOnFly),

    customerSelectionType: rule.customerSelectionType ?? 'all',

    contact: Boolean(rule.contactEnabled),
    contactEnabled: Boolean(rule.contactEnabled),
    contactUrl: rule.contactUrl ?? '',
    showHint: rule.showHint ?? true,

    messages: rule.messages ?? {},
    design: rule.design ?? {},

    // Pro feature A (docs/shopify-pro-features-spec.md §A) — must round-trip
    // back into form state, or every connector silently resets to "fixed"
    // on reload (the exact failure class CLAUDE.md calls out for adaptors).
    limitSources: rule.limitSources ?? undefined,
    limitsLastResolvedAt: rule.limitsLastResolvedAt ?? undefined,
    limitsResolvedSummary: rule.limitsResolvedSummary ?? undefined,

    // Pro feature B — must round-trip too, or the toggle silently resets on reload.
    stockCap: rule.stockCap ?? undefined,
    stockLastResolvedAt: rule.stockLastResolvedAt ?? undefined,

    hasPurchaseHistoryCondition: Boolean(rule.hasPurchaseHistoryCondition),
    purchaseHistory: rule.purchaseHistory ?? [],

    scopeProducts: fromProducts(rule.products),
    scopeCollections: fromCollections(rule.collections),
    scopeTags: fromTags(rule.tags),
    scopeVendors: fromVendors(rule.vendors),
    scopeProductTypes: fromProductTypes(rule.productTypes),
    scopePriceRanges: fromPriceRanges(rule.priceRanges),
    scopeVariantMetafieldConditions: fromVariantMetafieldConditions(rule.variantMetafieldConditions),

    // Reverse of save.adaptor.js's mapCustomers/mapCustomerTags/etc.
    //
    // The eligibility pickers key their selection off `id` and match it against
    // the list they fetch from Shopify, which is keyed by GID (or by the tag
    // string). Passing the backend rows straight through put the local row uuid
    // in `id` instead, so reopening a saved rule showed none of its chosen
    // customers, tags or segments as selected — they were stored correctly and
    // simply looked empty.
    customers: fromCustomers(rule.customers),
    customerTags: fromCustomerTags(rule.customerTags),
    customerSegments: fromCustomerSegments(rule.customerSegments),
    countries: fromCountries(rule.countries),

    // Backend-cached "Applies to" popover data (RuleActivationService.
    // reconcileOrEnqueue/applyResolvedScope) — computed once per save, not
    // re-resolved live on every list view. See RuleProductsCell.js.
    processingStatus: rule.processingStatus ?? 'idle',
    productsCount: rule.productsCount ?? null,
    variantsCount: rule.variantsCount ?? 0,
    scopeSnapshotProducts: rule.scopeSnapshot?.products ?? [],

    // Rule precedence (docs/rule-precedence-implementation-plan.md §0).
    // scope* = the raw scope before arbitration; productsCount above is what
    // the rule actually enforces. coverageAckHash must round-trip, or the
    // save review would re-open on every save of a partially-overridden rule.
    scopeProductsCount: rule.scopeProductsCount ?? null,
    scopeVariantsCount: rule.scopeVariantsCount ?? null,
    coverageAckHash: rule.coverageAckHash ?? null,
  };

  return { data, success: true, message: 'ok' };
}
